import React, { useState } from 'react';
import './MusicSearch.css';

function MusicSearch({ serverUrl, onSongImported }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [selectedSong, setSelectedSong] = useState(null);
  const [showLyrics, setShowLyrics] = useState(false);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    setSearchResults([]);

    try {
      const response = await fetch(
        `${serverUrl}/api/music/search?q=${encodeURIComponent(searchQuery)}`
      );

      if (response.ok) {
        const results = await response.json();
        console.log('Résultats de recherche:', results);
        setSearchResults(results);
        
        if (results.length === 0) {
          alert('Aucun résultat trouvé. Essayez avec d\'autres mots-clés.');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Erreur serveur:', response.status, errorData);
        alert(`Erreur serveur (${response.status}). Vérifiez que le serveur est démarré.`);
      }
    } catch (error) {
      console.error('Erreur de connexion:', error);
      alert('Impossible de se connecter au serveur. Vérifiez que le serveur backend est démarré sur le port 3501.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleImportSong = async (song) => {
    if (!song.url) {
      alert('Aucune URL disponible pour cette chanson');
      return;
    }

    try {
      setIsImporting(true);
      console.log('📥 Import YouTube:', song.title);
      
      const response = await fetch(`${serverUrl}/api/music/download-youtube`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          url: song.url,
          title: song.title,
          artist: song.artist,
          duration: song.duration,
          coverUrl: song.coverUrl,
          videoId: song.id
        })
      });

      if (response.ok) {
        const importedSong = await response.json();
        alert(`✅ Chanson importée: ${song.title}\n\nChanson complète téléchargée depuis YouTube !`);
        
        if (onSongImported) {
          onSongImported({
            ...importedSong,
            url: `${serverUrl}${importedSong.path}`,
            lyrics: song.lyrics
          });
        }
      } else {
        const error = await response.json();
        alert(`Erreur: ${error.error || 'Échec du téléchargement'}`);
      }
    } catch (error) {
      console.error('Erreur lors de l\'import:', error);
      alert('Erreur lors de l\'import de la chanson YouTube');
    } finally {
      setIsImporting(false);
    }
  };

  const handleShowLyrics = (song) => {
    setSelectedSong(song);
    setShowLyrics(true);
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="music-search">
      <div className="search-header">
        <h3>🌐 Rechercher des Chansons</h3>
        <p className="search-subtitle">
          YouTube (si Internet) + Bibliothèque Locale + Paroles LRCLib
        </p>
        <div className="search-info-box">
          <p>💡 <strong>Pour lancer une chanson complète :</strong></p>
          <ol>
            <li>Fermez cette recherche</li>
            <li>Cliquez sur "🔍 Bibliothèque locale"</li>
            <li>Sélectionnez une des 15 chansons</li>
            <li>Cliquez "▶️ Lecture" pour lancer</li>
          </ol>
          <p>Cette recherche permet de télécharger des chansons complètes depuis YouTube ou de voir si une chanson existe dans votre bibliothèque.</p>
        </div>
      </div>

      <div className="search-box-container">
        <input
          type="text"
          className="search-input-online"
          placeholder="Rechercher un titre, artiste..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
        />
        <button 
          className="btn btn-primary search-btn"
          onClick={handleSearch}
          disabled={isSearching || !searchQuery.trim()}
        >
          {isSearching ? '🔄 Recherche...' : '🔍 Rechercher'}
        </button>
      </div>

      {isSearching && (
        <div className="loading-results">
          <div className="spinner"></div>
          <p>Recherche en cours...</p>
        </div>
      )}

      {searchResults.length > 0 && (
        <div className="results-container">
          <div className="results-header">
            <span>{searchResults.length} résultat(s) trouvé(s)</span>
          </div>

          <div className="results-list">
            {searchResults.map((song, index) => (
              <div key={index} className="result-item">
                <div className="result-image">
                  {song.imageUrl || song.coverUrl ? (
                    <img src={song.imageUrl || song.coverUrl} alt={song.title} />
                  ) : (
                    <div className="result-image-placeholder">🎵</div>
                  )}
                </div>

                <div className="result-info">
                  <h4 className="result-title">{song.title}</h4>
                  <p className="result-artist">{song.artist}</p>
                  <div className="result-meta">
                    {song.album && <span className="meta-album">📀 {song.album}</span>}
                    {song.year && <span className="meta-year">📅 {song.year}</span>}
                    {song.duration && (
                      <span className="meta-duration">⏱️ {formatDuration(song.duration)}</span>
                    )}
                  </div>

                  <div className="result-badges">
                    {song.isLocal && (
                      <span className="badge badge-local">
                        💾 Dans la bibliothèque
                      </span>
                    )}
                    {song.hasLyrics && (
                      <span className="badge badge-lyrics">
                        📝 Paroles {song.hasSync && '(Sync)'}
                      </span>
                    )}
                    {song.fullAudioAvailable && (
                      <span className="badge badge-youtube">📺 YouTube</span>
                    )}
                    {song.album && !song.isLocal && (
                      <span className="badge badge-album">💿 {song.album}</span>
                    )}
                    {song.explicit && (
                      <span className="badge badge-explicit">🔞 Explicit</span>
                    )}
                  </div>
                </div>

                <div className="result-actions">
                  {song.isLocal && (
                    <div className="local-song-notice">
                      <p className="notice-text">
                        ✅ Cette chanson est dans votre bibliothèque !<br/>
                        <strong>Pour la lancer :</strong>
                      </p>
                      <ol className="notice-steps">
                        <li>Fermez cette recherche</li>
                        <li>Ouvrez "🔍 Bibliothèque locale"</li>
                        <li>Cliquez sur "{song.title}"</li>
                        <li>Puis "▶️ Lecture"</li>
                      </ol>
                    </div>
                  )}
                  
                  {song.hasLyrics && (
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleShowLyrics(song)}
                    >
                      📄 Voir les paroles
                    </button>
                  )}
                  
                  {song.fullAudioAvailable && !song.isLocal && (
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => handleImportSong(song)}
                      disabled={isImporting}
                    >
                      {isImporting ? '⏳ Téléchargement...' : '⬇️ Télécharger (Complet)'}
                    </button>
                  )}
                  
                  {song.permalink && !song.isLocal && (
                    <a
                      href={song.permalink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-info"
                    >
                      📺 Ouvrir sur YouTube
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showLyrics && selectedSong && (
        <div className="lyrics-modal" onClick={() => setShowLyrics(false)}>
          <div className="lyrics-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="lyrics-modal-header">
              <h3>{selectedSong.title}</h3>
              <p>{selectedSong.artist}</p>
              <button 
                className="close-btn"
                onClick={() => setShowLyrics(false)}
              >
                ✕
              </button>
            </div>

            <div className="lyrics-modal-body">
              {selectedSong.lyrics ? (
                <>
                  {selectedSong.lyrics.syncedLyrics ? (
                    <div className="lyrics-synced">
                      <div className="lyrics-type-badge">✨ Paroles Synchronisées</div>
                      <pre>{selectedSong.lyrics.syncedLyrics}</pre>
                    </div>
                  ) : selectedSong.lyrics.plainLyrics ? (
                    <div className="lyrics-plain">
                      <div className="lyrics-type-badge">📝 Paroles Texte</div>
                      <pre>{selectedSong.lyrics.plainLyrics}</pre>
                    </div>
                  ) : (
                    <p className="no-lyrics">Paroles non disponibles</p>
                  )}
                  
                  {selectedSong.lyrics.instrumental && (
                    <div className="instrumental-badge">🎸 Version Instrumentale</div>
                  )}
                </>
              ) : (
                <p className="no-lyrics">Aucune parole disponible</p>
              )}
            </div>

            <div className="lyrics-modal-footer">
              <button 
                className="btn btn-primary"
                onClick={() => setShowLyrics(false)}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MusicSearch;
